import type { ReactNode } from 'react'

/**
 * One coloured strip of a block — Anfahrt, Arbeit, Abfahrt, or the whole of a Pause.
 *
 * The vertical twin of the board's `SegmentCell`, down to the shape of the density rule it serves.
 * It takes its height rather than deriving it, because `distributeSegmentHeights` has already split
 * the block's box between the strips: the caller knows what each one got, and so the density ladder
 * and the paint are reading the same number.
 */

/**
 * The rule between two strips, drawn as a border so `border-box` charges it to the strip's own
 * height. The old blocks used a 1px flex gap, which let the grid lines behind show *through* a block
 * and made one job read as three chips stacked by coincidence.
 */
const DIVIDER = '1px solid rgba(255,255,255,0.28)'

/**
 * Darkens the fill under the text.
 *
 * The palette in `shared/lib/timelineColors` is shared with the disposition board and is not this
 * component's to change — but white on `#2EB5D9` is 2.4:1 and on `#3BC67D` 2.2:1, which is not text
 * on a device that gets used outdoors. Scrimming the fill keeps the hue that identifies the status
 * and buys the contrast back: every status colour lands at or above 4.5:1 through this gradient.
 * Slightly heavier at the top, where the time sits.
 */
const SCRIM = 'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.32) 100%)'

/** Belt to the scrim's braces — for the pixel where a glyph lands on a bright patch anyway. */
const TEXT_SHADOW = '0 1px 2px rgba(0,0,0,0.5)'

interface Props {
  color: string
  /** From `distributeSegmentHeights`. Zero means the segment is absent and nothing is drawn. */
  heightPx: number
  /** A rule where this strip meets the one above it. */
  divided?: boolean
  /**
   * A white inset ring, pulsing — the running job's marker of last resort, worn at every density
   * because it survives on a strip too short to hold anything inside it.
   */
  ringed?: boolean
  /**
   * Off for a strip so short that 3px of padding is a quarter of it — an icon centred in the full
   * height reads, the same icon squeezed into what padding leaves does not.
   */
  padded?: boolean
  children?: ReactNode
}

export const BlockSegment = ({
  color,
  heightPx,
  divided = false,
  ringed = false,
  padded = true,
  children,
}: Props) => {
  if (heightPx <= 0) return null

  const hasText = Boolean(children)

  return (
    <div
      className="relative shrink-0 overflow-hidden"
      style={{
        height: heightPx,
        backgroundColor: color,
        borderTop: divided ? DIVIDER : undefined,
      }}
    >
      {hasText && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: SCRIM }}
          aria-hidden
        />
      )}

      {hasText && (
        <div
          className={`relative flex h-full flex-col justify-center overflow-hidden px-1.5 text-white ${
            padded ? 'py-[3px]' : 'py-0'
          }`}
          style={{ textShadow: TEXT_SHADOW }}
        >
          {children}
        </div>
      )}

      {ringed && (
        <div
          className="pointer-events-none absolute inset-0 animate-pulse ring-2 ring-inset ring-white"
          aria-hidden
        />
      )}
    </div>
  )
}

/*
 * The three text sizes a block has. Each sits on a 13px line box — the same `LINE_PX` the density
 * ladder counts with, which is the only reason "two rows fit" is a fact rather than a hope.
 *
 * Hierarchy is size and weight, not opacity: a faded row on a scrimmed fill drops back under 4.5:1
 * and secondary information is not less legible information.
 */

/** The time. Tabular so the column of start times aligns down the day. */
export const TIME_CLASS = 'truncate text-[13px] font-bold leading-[13px] tabular-nums'

/** What the job is — the location, or the task's title. */
export const SUBJECT_CLASS = 'truncate text-[12px] font-semibold leading-[13px]'

/** Who and how to reach them. */
export const META_CLASS = 'truncate text-[11px] font-normal leading-[13px]'

/** Anfahrt / Abfahrt / Transfer, and the labels on a Pause. */
export const STRIP_CLASS = 'truncate text-[11px] font-semibold leading-[13px]'

/**
 * The same four, for the one-day view: a full-width block, read at arm's length on a phone, was
 * too small at 13/12/11. Same weights, same roles, three to two pixels larger on a 17px line box —
 * `WIDE_LINE_PX`, which the density ladder counts with, so the rows it promises are the rows drawn.
 */
export const TEXT_WIDE = {
  time: 'truncate text-[16px] font-bold leading-[17px] tabular-nums',
  subject: 'truncate text-[15px] font-semibold leading-[17px]',
  meta: 'truncate text-[14px] font-normal leading-[17px]',
  strip: 'truncate text-[13px] font-semibold leading-[17px]',
} as const

export const TEXT_NARROW = {
  time: TIME_CLASS,
  subject: SUBJECT_CLASS,
  meta: META_CLASS,
  strip: STRIP_CLASS,
} as const

export const textFor = (wide: boolean) => (wide ? TEXT_WIDE : TEXT_NARROW)
